import { DictionaryService } from './../../../core/service/dictionary.service';
import { SettingService } from './../../../core/service/setting.service';
import { MessageSubject, MessengerService } from 'src/app/core/service/messenger.service';
import { CaseService } from 'src/app/core/service/case.service';
import { Component, ElementRef, Input, OnDestroy, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { AppConfigService, AppConfiguration } from 'src/app/core/service/app-config.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { TaskUtility } from 'src/app/task/task.utility';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { User } from 'src/app/user/model/user';
import { IntlService } from '@progress/kendo-angular-intl';
import { ChannelService } from 'src/app/core/service/channel.service';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { TextProcessor } from 'src/app/core/utility/text.processor';
import { Subscription } from 'rxjs';
import { UserService } from 'src/app/core/service/user.service';
import { TranslateService } from '@ngx-translate/core';
import { MemberService } from 'src/app/core/service/member.service';
import { ReferralComponent, ReferralEvent } from 'src/app/referral/referral.component';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { saveAs } from '@progress/kendo-file-saver';

@Component({
  standalone: false,
  selector: 'app-case-detail-summary',
  templateUrl: './case-detail-summary.component.html',
  styleUrls: ['./case-detail-summary.component.scss']
})
export class CaseDetailSummaryComponent implements OnDestroy
{
  @ViewChild('referral') public referral: ReferralComponent;
  @ViewChildren(TooltipDirective) public tooltipDir: QueryList<TooltipDirective>;

  /**
   * Subject/Patient of the case.
   */
  @Input() public member: any;

  /**
   * Case ID.
   */
  @Input() public id: number;

  /**
   * Data model for this component.
   */
  public model: any;

  /**
   * Current logged in userId
   */
  public currentUserId: number;
  public clinicalSummaryEnabled = false;
  public windowState = 'default';
  public pageSizes = [5, 10, 25, 50, 100];
  public communityEnabled = false;
  public isOperatorRole = false;
  public isCareCoordinatorOrProgramLead = false;
  public savingMessage = false;
  public sensitivitiesText: string;
  public referralsCollapsed = false;
  public hasExternalSearchSources = false;
  public mrnStrategy: string;

  /**
   * Controls the dialog for assign the case manager.
   */
  public assignCaseManagerDialogOpened = false;

  /**
   * Controls the dialog for closing the case.
   */
  public closeCaseDialogOpened = false;

  /**
   * Controls the dialog for changing the case priority.
   */
  public changeCasePriorityDialogOpened = false;

  /**
   * The reason for closing this case.
   */
  public reasonForClose = null;

  /**
   * The new workflow status on close case
   */
  public channelWorkflowStatusId = null;

  /**
   * Indicates whether the task entries are shown in the timeline.
   */
  public includeTasksInTimeline = true;

  /**
   * Indicates whether the message entries are shown in the timeline.
   */
  public includeMessagesInTimeline = true;

  /**
   * Controls the dialog for messages.
   */
  public messageDialogOpened = false;

  /**
   * When composing a new message, indicate the default message destination username.
   */
  public messageSendTo = '';

  /**
   * Controls the dialog for message detail.
   */
  public messageDetailDialogOpened = false;

  /**
   * Selected message.
   */
  public selectedMessage = null;

  public taskCount = 0;
  public taskView = new GridViewManager();
  public taskFilters: Array<{ text: string; value: string }>;

  public messageEnableAdvanced = false;
  public messageCount = 0;
  public messageView = new GridViewManager();

  public selectedTaskFilter = 'OPEN';
  public editDataItemAppointment: any;
  public memberId: string;
  public caseId: number;
  public caseTasksCollapsed = false;
  public caseMessagesCollapsed = false;
  public caseDocumentsCollapsed = false;
  public accessEditWorkflow = false;
  public accessEditSensitivities = false;
  public workflowStatusEdit = false;
  public sensitivitiesEdit = false;
  public workflowStatuses = [];
  public channelId: number;
  public workflowStatusEditForm: UntypedFormGroup;
  public sensitivitiesEditForm: UntypedFormGroup;
  public messagesEnabled = false;
  public otherCasesEnabled = false;
  public attachmentsEnabled = false;
  public caseHistoryEnabled = false;
  public referralsEnabled = false;
  public addReferralsEnabled = false;
  public consentableDataTypes = [];
  public isNewWorkflowStatus = false;

  /**
   * Task view type
   */
  public taskViewType = 'list';

  /**
   * When creating a new task, indicate the default task assignee.
   */
  public createTaskAssigneeId = '';

  /**
   * Is it a new task?
   */
  public isNewTask = false;

  /**
   * The select task from the tasks table.
   */
  public selectedTask: any = null;

  /**
   * When true, auto complete if there is no outcomes.
   */
  public autoComplete = false;

  /**
   * Controls the Task detail modal
   */
  public taskOpened = false;

  private appConfig: AppConfiguration;
  private messagingSubscription: Subscription;
  private currentUser: User;

  constructor(
    private caseService: CaseService,
    private appConfigService: AppConfigService,
    private messengerService: MessengerService,
    private messageHandlerService: MessageHandlerService,
    private taskUtility: TaskUtility,
    private authenticationService: AuthenticationService,
    private settingService: SettingService,
    private userService: UserService,
    private dictionaryService: DictionaryService,
    private channelService: ChannelService,
    private formBuilder: UntypedFormBuilder,
    private translateService: TranslateService,
    private memberService: MemberService,
    public intl: IntlService)
  {
    this.appConfig = this.appConfigService.getConfig();
    this.clinicalSummaryEnabled = this.appConfig.clinicalViewer &&
      this.appConfig.clinicalViewer.baseUrl !== '' && this.appConfig.clinicalViewer.baseUrl !== null;

    this.messagingSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        // subscribe to open task dialog from task card view.
        if (message.type === 'openTaskEditDialog' && message.data && message.data.task)
        {
          this.openTask(message.data.task, message.data.autoComplete);
        }
        else
          // subscribe to open new message dialog from task card view.
          if (message.type === 'openComposeMessageDialog' && message.data)
          {
            const isDirectAddress = message.data !== undefined && message.data.isDirectAddress !== undefined ?
              message.data.isDirectAddress : false;
            this.composeMessage(message.data !== undefined && message.data.sendTo ? message.data.sendTo : null, isDirectAddress);
          }
          // subscribe to create new task dialog from task card view.
          else if (message.type === 'openAddTaskDialog' && message.data)
          {
            this.createTask(message.data.assigneeId);
          }
          else if (message.type === 'openAppointmentEditDialog' && message.data)
          {
            this.memberId = message.data.memberId;
            this.editDataItemAppointment = message.data.editDataItemAppointment;
          }
          else if (message.type === 'reloadMemberAssessments')
          {
            this.delayedRefreshCaseDetails();
          }
          else if (message.type === 'newReferralForCategory' && message.data && message.data.categoryId)
          {
            setTimeout(() =>
            {
              this.messengerService.sendMessage(new MessageSubject('searchForServicesToReferred', {
                categories: [message.data.categoryId],
                referralReassign: true,
                context: 'case',
                parentCaseId: message.data.caseId,
                excludedServices: [message.data.excludeReferredServiceId],
              }));
            }, 500);
          }
      }
    });
  }

  @Input() public set data(value: any)
  {
    const user = this.authenticationService.currentUser;
    this.currentUserId = user ? user.id : 0;
    this.currentUser = user;

    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.mrnStrategy = sessionSetting.mrnStrategy;
        this.communityEnabled = sessionSetting.communityEnabled;
        this.isOperatorRole = user.role === 'ROLE_OPERATOR';
        this.isCareCoordinatorOrProgramLead = user.role === 'ROLE_CARE_COORDINATOR' || user.role === 'ROLE_PROGRAM_LEAD';
        this.accessEditWorkflow = (sessionSetting.workflowStatusEditRoles !== '') ?
          this.userService.isRoleInList(this.currentUser.role, sessionSetting.workflowStatusEditRoles) : false;
        this.accessEditSensitivities = (sessionSetting.sensitivitiesEditRoles !== '') ?
          this.userService.isRoleInList(this.currentUser.role, sessionSetting.sensitivitiesEditRoles) : false;
        this.messagesEnabled = (sessionSetting.messagesRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.messagesRoles) : false;
        this.otherCasesEnabled = (sessionSetting.otherCasesRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.otherCasesRoles) : false;
        this.attachmentsEnabled = (sessionSetting.attachmentsRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.attachmentsRoles) : false;
        this.caseHistoryEnabled = (sessionSetting.caseHistoryRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.caseHistoryRoles) : false;
        this.referralsEnabled = sessionSetting.communityEnabled && ((sessionSetting.referralsRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.referralsRoles) : false);
        this.addReferralsEnabled = (sessionSetting.addCaseReferralsRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.addCaseReferralsRoles) : false;

        this.dictionaryService.getCachedPatientSearchSources().subscribe({
          next: (data) =>
          {
            this.hasExternalSearchSources = data.filter(x => x.code !== 'CHANNELS').length > 0;
          }
        });

        this.model = value;

        if (this.model.tasks !== undefined)
        {
          this.taskView.startLoading();
          this.taskView.setData(this.model.tasks);
          this.taskView.setSort([{ field: 'name', dir: 'asc' }]);
          this.taskCount = this.model.tasks.length;
          this.taskView.stopLoading();
        }

        if (this.model.messages !== undefined)
        {
          this.messageView.startLoading();
          this.messageView.setData(this.model.messages);
          this.messageCount = this.model.messages.length;
          this.messageView.stopLoading();
        }

        // Setting memberId and caseId to null will force component refresh
        this.memberId = null;
        this.caseId = null;
        setTimeout(() =>
        {
          this.caseId = this.id;
          this.memberId = value.member.id;
        });

        this.taskFilters = this.taskUtility.taskFilters;
        this.taskFilterSelected(this.selectedTaskFilter, true);
        this.setSensitivitiesText();
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public ngOnDestroy(): void
  {
    if (this.messagingSubscription !== undefined) { this.messagingSubscription.unsubscribe(); }
  }

  /**
   * Change the status of the case to "Closed".
   */
  public closeCase(): void
  {
    this.translateService.get('CASE-CLOSED-SUCCESSFULLY').subscribe({
      next: (res) =>
      {
        this.caseService.closeCase(this.model.id, this.reasonForClose,
          this.channelWorkflowStatusId ? this.channelWorkflowStatusId : null, this.channelId)
          .subscribe({
            next: () =>
            {
              this.model.status = 'Closed';
              this.reasonForClose = null;
              this.channelWorkflowStatusId = null;
              this.channelId = null;
              this.getCaseDetailsData();
              this.messageHandlerService.displayDirectMessage('success', res.replace('{{caseId}}', this.model.id));
            },
            error: (errorResponse) =>
            {
              this.messageHandlerService.displayError(errorResponse);
            },
            complete: () =>
            {
              this.closeCaseDialogOpened = false;
            }
          });
      }
    });
  }

  /**
   * Opens the external Clinical Viewer link in a new browser tab.
   */
  public openClinicalViewer(): void
  {
    const url = `${this.appConfig.clinicalViewer.baseUrl}?FromPortal=1&MRN=${this.member.mrn}`;
    window.open(url, '_blank');
  }

  /**
   * Updates the Case history entries in the timeline.
   */
  public updateTimelineEntries(): void
  {
    this.messengerService.sendMessage(new MessageSubject('timelineVisibilityChanged', {
      includeTasksInTimeline: this.includeTasksInTimeline,
      includeMessagesInTimeline: this.includeMessagesInTimeline
    }));
  }

  /**
   * Create a new message.
   */
  public composeMessage(sendTo: string = null, isDirectAddress = false): void
  {
    this.messageSendTo = isDirectAddress ? `direct-${sendTo}` : sendTo;
    this.messageDialogOpened = true;
  }

  /**
   * Opens the selected message in a modal.
   *
   * @param message The message to open.
   */
  public openMessageDetails(message): void
  {
    this.selectedMessage = message;
    this.messageDetailDialogOpened = true;
  }

  /**
   * Send message to case-details.component to force a case reload.
   */
  public getCaseDetailsData(): void
  {
    this.messengerService.sendMessage(new MessageSubject('getCaseDetailsData'));
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public messageDataStateChange(state: DataStateChangeEvent): void
  {
    this.messageView.setState(state);
  }

  /**
   * Task filter has been selected.
   *
   * @param filter Filter: Open, Completed, All
   */
  public taskFilterSelected(filter: string, isOnInit = false): void
  {
    this.selectedTaskFilter = filter;
    const filterChanged: any = {};
    filterChanged.filter = filter;
    if (!isOnInit)  // This is to prevent two getCaseDetails() calls
    {
      this.messengerService.sendMessage(new MessageSubject('taskFilterChanged', filterChanged));
    }
  }

  public saveAppointmentChangesHandler(): void
  {
    this.editDataItemAppointment = undefined;
    this.messengerService.sendMessage(new MessageSubject('getCaseDetailsData'));
  }

  public cancelAppointmentChangesHandler(): void
  {
    this.editDataItemAppointment = undefined;
  }

  public cancelCloseCase(): void
  {
    this.closeCaseDialogOpened = false;
    this.reasonForClose = null;
    this.channelWorkflowStatusId = null;
    this.channelId = null;
  }

  public openCloseCaseDialog(): void
  {
    this.workflowStatuses = [];
    this.channelId = null;
    this.channelWorkflowStatusId = null;

    // Attempt to get case for consent security reason only
    this.caseService.getCaseDetails(this.id, 'SUMMARY').subscribe({
      next: (caseData) =>
      {
        if (caseData.channelCategoryWorkflowStatusId !== null)
        {
          this.dictionaryService.getCodesByCodeSetId(caseData.channelCategoryWorkflowStatusId).subscribe({
            next: (codes) =>
            {
              this.channelId = caseData.channelId;
              this.workflowStatuses = caseData.channelWorkflowStatus ?
                codes.filter(x => x.id !== caseData.channelWorkflowStatus.id) : codes;
              this.closeCaseDialogOpened = true;
            }
          });
        }
        else
        {
          this.closeCaseDialogOpened = true;
        }
      }
    });
  }

  /**
   * Edit the workflow status on the closed case.
   *
   * @param channelId ChannelId of the case.
   */
  public editWorkflowStatus(channelId: number): void
  {
    this.channelService.getChannel(channelId).subscribe({
      next: (channel) =>
      {
        if (channel.channelCategory && channel.channelCategory.channelWorkflowStatus)
        {
          this.dictionaryService.getCodesByCodeSetId(channel.channelCategory.channelWorkflowStatus.id).subscribe({
            next: (codes) =>
            {
              this.isNewWorkflowStatus = this.model?.channelWorkflowStatus?.id === undefined;
              this.workflowStatuses = codes.filter(x => x.id !== this.model?.channelWorkflowStatus?.id);
              this.workflowStatusEditForm = this.formBuilder.group({
                workflowStatus: [null, Validators.required],
              });
              this.workflowStatusEdit = true;
            }
          });
        }
        else
        {
          this.messageHandlerService.displayWarning('NO-WORKFLOW-STATUS-FOUND');
        }
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public editCaseSensitivities(caseSensitivities: []): void
  {
    // Get Consentable Data Types
    this.dictionaryService.getCachedConsentableDataTypes().subscribe({
      next: (data) =>
      {
        this.consentableDataTypes = data;
        this.sensitivitiesEditForm = this.formBuilder.group({
          sensitivities: [caseSensitivities],
        });
        this.sensitivitiesEdit = true;
      }
    });
  }

  /**
   * Check if item is selected.
   *
   * @param filterItem Filter to check.
   * @param item Item value to check.
   */
  public isSensitivitiesSelected(filterItem: string, itemValue: any): boolean
  {
    return this.sensitivitiesEditForm.get(filterItem) &&
      this.sensitivitiesEditForm.get(filterItem).value &&
      this.sensitivitiesEditForm.get(filterItem).value.map(x => x.id).includes(itemValue);
  }

  /**
   * Close the workflow status edit dialog
   */
  public closeWorkflowStatusEdit(): void
  {
    this.workflowStatusEdit = false;
  }

  /**
   * Close the sensitivities edit dialog
   */
  public closeSensitivitiesEdit(): void
  {
    this.sensitivitiesEdit = false;
  }

  /**
   * Update the workflow status for the case.
   */
  public updateWorkflowStatus(): void
  {
    const putObject: any = {};
    putObject.workflowStatus = this.workflowStatusEditForm.get('workflowStatus').value;
    this.caseService.updateWorkflowStatus(putObject, this.id).subscribe({
      next: () =>
      {
        this.messageHandlerService.displaySuccess('WORKFLOW-STATUS-SAVED-SUCCESSFULLY');
        this.getCaseDetailsData();
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      },
      complete: () =>
      {
        this.closeWorkflowStatusEdit();
      }
    });
  }

  /**
   * Update the sensitivities for the case.
   */
  public updateSensitivities(): void
  {
    const putObject: any = {};
    putObject.sensitivities = this.sensitivitiesEditForm.get('sensitivities').value;
    this.caseService.updateSensitivities(putObject, this.id).subscribe({
      next: () =>
      {
        this.messageHandlerService.displaySuccess('SENSITIVITIES-SAVED-SUCCESSFULLY');
        this.getCaseDetailsData();
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      },
      complete: () =>
      {
        this.closeSensitivitiesEdit();
      }
    });
  }

  /**
   * Open the new task dialog
   */
  public createTask(assigneeId: string = null): void
  {
    this.createTaskAssigneeId = assigneeId;
    this.isNewTask = true;
    this.selectedTask = null;
  }

  /**
   * Opens the task detail modal.
   *
   * @param task Task object
   */
  public openTask(task: any, isAutoComplete = false): void
  {
    this.autoComplete = isAutoComplete;
    this.selectedTask = task;
    this.selectedTask.name = TextProcessor.truncateText(this.selectedTask.name, 70);
    this.taskOpened = true;
    document.body.style.overflow = 'hidden';
  }

  /**
   * Event handler when attempting to complete that and No was selected from the confirmation dialog.
   * If the complete dialog was automatically open (autoComplete=true) then the task detail dialog will
   * close automatically.
   *
   * @param taskId Task ID
   */
  public closeTaskDetail(): void
  {
    this.closeTaskDialog();
  }

  public closeTaskDialog(): void
  {
    this.taskOpened = false;
    this.getCaseDetailsData();
    // If Task Card view is selected, perform a reload to make sure it refreshes after any updates on the task.
    if (this.taskViewType === 'card')
    {
      this.messengerService.sendMessage(new MessageSubject('refreshTaskCardView', { tasks: this.model.tasks }));
    }
    document.body.style.overflow = '';
  }

  /**
   * Event handler for task completion.
   */
  public onTaskCompleted(): void
  {
    this.closeTaskDialog();
    this.getCaseDetailsData();
  }

  /**
   * Method called after a new task has been added.
   */
  public afterTaskAdded(): void
  {
    this.isNewTask = false;
    // send message to update case history timeline (Case UI)
    this.messengerService.sendMessage(new MessageSubject('caseHistoryUpdated'));
  }

  public updateTasks(update: boolean): void
  {
    if (update)
    {
      this.getCaseDetailsData();
    }
  }

  public referralsOpenStateChanged(e: boolean): void
  {
    this.referralsCollapsed = e;
  }

  public newReferral(): void
  {
    this.referral.newReferral();
  }

  public getReferralHistory(): void
  {
    this.memberService.requestReferralsHistory(this.member.id).subscribe({
      complete: () =>
      {
        this.messageHandlerService.displaySuccess('REQUEST-REFERRAL-HISTORY-SENT');
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * After processing the Referral, the case will be completed if this is a referral reassignment.
   */
  public afterReferralProcessing(e: ReferralEvent): void
  {
    this.referral.completeReferralReassigment(e);
    if (e && e.referralReassign && e.parentCaseId > 0) 
    {
      this.getCaseDetailsData();
    }
  }

  public showTooltip(e: ElementRef, tooltip: ElementRef): void
  {
    const tooltipDirective = this.tooltipDir.toArray().find(t => t.tooltipWrapper.nativeElement === tooltip);
    if (tooltipDirective)
    {
      tooltipDirective.toggle(e, true); // Show the tooltip
    }
  }

  public hideTooltip(tooltip: ElementRef): void
  {
    const tooltipDirective = this.tooltipDir.toArray().find(t => t.tooltipWrapper.nativeElement === tooltip);
    if (tooltipDirective)
    {
      tooltipDirective.hide(); // Show the tooltip
    }
  }

  public downloadBpmn(): void
  {
    this.caseService.getBusinessProcess(this.id).subscribe({
      next: (data) =>
      {
        if (data && data.size > 0)
        {
          this.translateService.get('DOWNLOAD-BPMN-FILENAME').subscribe({
            next: (res) =>
            {
              saveAs(`data: ${data.mimeType}; base64, ${data.content}`, `${res.replace('{{caseId}}', this.id)}`);
              this.messageHandlerService.displaySuccess('DOWNLOAD-BPMN-DOWNLOADED-SUCCESSFULLY');
            }
          });
        }
        else
        {
          this.messageHandlerService.displayError('DOWNLOAD-BPMN-FAILED');
        }
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * This method is called when processing assessment fulfillments on the server-side. We will attempt to refresh the case extensions after 20 seconds. 
   * It will be called on new or updated assessment fulfillment.
   */
  public delayedRefreshCaseDetails(): void
  {
    setTimeout(() => { this.getCaseDetailsData(); }, 20000);
  }

  private setSensitivitiesText(): void
  {
    this.sensitivitiesText = '';
    if (this.model)
    {
      const texts = this.model.sensitivities.map((s: any) => (s.displayName));
      this.sensitivitiesText = texts.sort().join(', ');
    }
  }
}
